import { differenceInHours } from 'date-fns';

export interface OTCostInput {
  startTime: Date;
  endTime: Date;
  dayRate: number;
  hourlyRate: number;
  tier: 0 | 1;
  otStartHrs?: number;
  dayMultiplier?: number;
  otMultiplier?: number;
}

// Same fields as OTCostInput but with hours worked given directly instead of
// a start/end pair — used to bill a day-rate/hourly-rate charge once against
// hours summed across several calls (e.g. a day-rate role spanning setup +
// show + strike), rather than once per call.
export interface OTCostFromHoursInput {
  hoursWorked: number;
  dayRate: number;
  hourlyRate: number;
  tier: 0 | 1;
  otStartHrs?: number;
  dayMultiplier?: number;
  otMultiplier?: number;
}

export interface OTCostOutput {
  hoursWorked: number;
  regularHours: number;
  overtimeHours: number;
  regularCost: number;
  overtimeCost: number;
  totalCost: number;
  tier: 0 | 1;
}

// Default penalty tiers: tier 0 = no day-rate penalty but the standard 1.5x
// OT premium still applies past otStartHrs; tier 1 = 1.5x day / 2.0x OT for
// day rates (an additional penalty on top of the standard OT premium).
const DEFAULT_TIER_SYSTEM: Record<0 | 1, { dayMultiplier: number; otMultiplier: number }> = {
  0: { dayMultiplier: 1.0, otMultiplier: 1.5 },
  1: { dayMultiplier: 1.5, otMultiplier: 2.0 },
};

function splitRegularOvertime(hoursWorked: number, otStartHrs: number) {
  return {
    regularHours: Math.min(hoursWorked, otStartHrs),
    overtimeHours: Math.max(0, hoursWorked - otStartHrs),
  };
}

export function costFromDayRateHours(input: OTCostFromHoursInput): OTCostOutput {
  const {
    hoursWorked,
    dayRate,
    tier,
    otStartHrs = 10,
    dayMultiplier = DEFAULT_TIER_SYSTEM[tier].dayMultiplier,
    otMultiplier = DEFAULT_TIER_SYSTEM[tier].otMultiplier,
  } = input;

  const { regularHours, overtimeHours } = splitRegularOvertime(hoursWorked, otStartHrs);

  // A day rate is a flat charge for the commitment, not an hourly proration —
  // any call time up to otStartHrs bills the full day rate once, regardless
  // of whether the actual call is 2 hours or 10. Only hours past otStartHrs
  // are billed incrementally, at an hourly-equivalent rate derived from the
  // day rate spread over otStartHrs hours.
  const hourlyEquivalent = dayRate / otStartHrs;
  const regularCost = regularHours > 0 ? dayRate * dayMultiplier : 0;
  const overtimeCost = overtimeHours * hourlyEquivalent * otMultiplier;
  const totalCost = regularCost + overtimeCost;

  return {
    hoursWorked,
    regularHours,
    overtimeHours,
    regularCost,
    overtimeCost,
    totalCost,
    tier,
  };
}

export function costFromHourlyRateHours(input: OTCostFromHoursInput): OTCostOutput {
  const {
    hoursWorked,
    hourlyRate,
    tier,
    otStartHrs = 10,
    dayMultiplier = DEFAULT_TIER_SYSTEM[tier].dayMultiplier,
  } = input;

  const { regularHours, overtimeHours } = splitRegularOvertime(hoursWorked, otStartHrs);

  // The tier multiplier (if any) elevates the regular rate; OT is the
  // standard 1.5x of the BASE rate, or the tier-elevated regular rate,
  // whichever is higher — so a tier-1 (penalty) worker's overtime is never
  // less than what they're already being paid for regular hours, but tier 1
  // doesn't stack an extra 1.5x on top of its own elevated rate either.
  const adjustedHourlyRate = hourlyRate * dayMultiplier;
  const adjustedOTRate = Math.max(adjustedHourlyRate, hourlyRate * 1.5);

  const regularCost = regularHours * adjustedHourlyRate;
  const overtimeCost = overtimeHours * adjustedOTRate;
  const totalCost = regularCost + overtimeCost;

  return {
    hoursWorked,
    regularHours,
    overtimeHours,
    regularCost,
    overtimeCost,
    totalCost,
    tier,
  };
}

export function costFromDayRate(input: OTCostInput): OTCostOutput {
  const hoursWorked = differenceInHours(input.endTime, input.startTime, { roundingMethod: 'ceil' });
  return costFromDayRateHours({ ...input, hoursWorked });
}

export function costFromHourlyRate(input: OTCostInput): OTCostOutput {
  const hoursWorked = differenceInHours(input.endTime, input.startTime, { roundingMethod: 'ceil' });
  return costFromHourlyRateHours({ ...input, hoursWorked });
}

// Calculate cost for a work period using day rate if day billing, hourly otherwise
export function calculateWorkCost(
  startTime: Date,
  endTime: Date,
  dayRate: number,
  hourlyRate: number,
  tier: 0 | 1,
  useDayRate: boolean = true,
  otStartHrs: number = 10,
): OTCostOutput {
  if (useDayRate) {
    return costFromDayRate({
      startTime,
      endTime,
      dayRate,
      hourlyRate,
      tier,
      otStartHrs,
    });
  } else {
    return costFromHourlyRate({
      startTime,
      endTime,
      dayRate,
      hourlyRate,
      tier,
      otStartHrs,
    });
  }
}

// ---- Standard Timekeeping (Planning/Timekeeping.md) — opt-in, parallel to
// everything above. Nothing in this section is called unless a rate-card
// role has useStandardTimekeeping set (see standard-timekeeping.ts), so the
// legacy single-tier functions above are never touched by it.

export interface BandSplit {
  regularHours: number;
  ot1Hours: number;
  ot2Hours: number;
}

// Three-band split: regular up to ot1StartHrs, ot1 between ot1StartHrs and
// ot2StartHrs, ot2 beyond ot2StartHrs. ot2StartHrs <= ot1StartHrs would
// produce a negative ot1 band, which doesn't correspond to any real policy
// this engine models — callers are expected to pass a sane pair (the
// standard-timekeeping.ts bucketing functions always do), so this doesn't
// defensively clamp that case.
export function splitIntoBands(hoursWorked: number, ot1StartHrs: number, ot2StartHrs: number): BandSplit {
  const regularHours = Math.min(hoursWorked, ot1StartHrs);
  const ot1Hours = Math.min(Math.max(0, hoursWorked - ot1StartHrs), ot2StartHrs - ot1StartHrs);
  const ot2Hours = Math.max(0, hoursWorked - ot2StartHrs);
  return { regularHours, ot1Hours, ot2Hours };
}

export interface StandardCostOutput {
  hoursWorked: number;
  regularHours: number;
  ot1Hours: number;
  ot2Hours: number;
  regularCost: number;
  ot1Cost: number;
  ot2Cost: number;
  totalCost: number;
  isHoliday: boolean;
  insufficientRest: boolean;
}

export interface StandardHourlyCostInput {
  hoursWorked: number;
  hourlyRate: number;
  ot1StartHrs?: number;
  ot2StartHrs?: number;
  ot1Multiplier?: number;
  ot2Multiplier?: number;
  isHoliday?: boolean;
}

// Timekeeping.md: "Overtime is billed at 1.5x after 8 hours and 2x after 12
// hours." Holiday: "bill as overtime for the entire call/day, regardless of
// hours worked" — read as 100% of hours at the first-tier (1.5x) rate,
// skipping the normal band split entirely (2x is always described
// separately as the explicit 12-hour tier, not "overtime" generically).
export function costFromHourlyRateHoursStandard(input: StandardHourlyCostInput): StandardCostOutput {
  const { hoursWorked, hourlyRate, ot1StartHrs = 8, ot2StartHrs = 12, ot1Multiplier = 1.5, ot2Multiplier = 2.0, isHoliday = false } = input;

  if (isHoliday) {
    const totalCost = hoursWorked * hourlyRate * ot1Multiplier;
    return {
      hoursWorked,
      regularHours: 0,
      ot1Hours: hoursWorked,
      ot2Hours: 0,
      regularCost: 0,
      ot1Cost: totalCost,
      ot2Cost: 0,
      totalCost,
      isHoliday: true,
      insufficientRest: false,
    };
  }

  const { regularHours, ot1Hours, ot2Hours } = splitIntoBands(hoursWorked, ot1StartHrs, ot2StartHrs);
  const regularCost = regularHours * hourlyRate;
  const ot1Cost = ot1Hours * hourlyRate * ot1Multiplier;
  const ot2Cost = ot2Hours * hourlyRate * ot2Multiplier;

  return {
    hoursWorked,
    regularHours,
    ot1Hours,
    ot2Hours,
    regularCost,
    ot1Cost,
    ot2Cost,
    totalCost: regularCost + ot1Cost + ot2Cost,
    isHoliday: false,
    insufficientRest: false,
  };
}

export interface StandardDayCostInput {
  hoursWorked: number;
  dayRate: number;
  ot1StartHrs?: number;
  ot2StartHrs?: number;
  ot1Multiplier?: number;
  ot2Multiplier?: number;
  // <restPeriodHrs since the prior calendar-day bucket ended — multiplies
  // the regular (flat day-rate) charge by 1.5, mirroring the existing
  // tier-1 dayMultiplier but auto-driven off the actual rest gap instead of
  // a manual flag. Timekeeping.md: "otherwise consecutive days will be
  // billed at 1.5 times the day rate."
  insufficientRest?: boolean;
  isHoliday?: boolean;
}

// Timekeeping.md: "Overtime is calculated after the 10th hour from the
// start of the day as (day rate /10 * 1.5) until the 12th hour, and
// (day rate /10 * 2) after the 12th hour." Holiday: flat dayRate * 1.5,
// independent of hours worked, skipping the normal day/OT split (same 1.5x
// first-tier interpretation as the hourly holiday case above).
export function costFromDayRateHoursStandard(input: StandardDayCostInput): StandardCostOutput {
  const {
    hoursWorked,
    dayRate,
    ot1StartHrs = 10,
    ot2StartHrs = 12,
    ot1Multiplier = 1.5,
    ot2Multiplier = 2.0,
    insufficientRest = false,
    isHoliday = false,
  } = input;

  if (isHoliday) {
    const totalCost = dayRate * ot1Multiplier;
    return {
      hoursWorked,
      regularHours: 0,
      ot1Hours: 0,
      ot2Hours: 0,
      regularCost: 0,
      ot1Cost: totalCost,
      ot2Cost: 0,
      totalCost,
      isHoliday: true,
      insufficientRest: false,
    };
  }

  const { regularHours, ot1Hours, ot2Hours } = splitIntoBands(hoursWorked, ot1StartHrs, ot2StartHrs);
  const hourlyEquivalent = dayRate / ot1StartHrs;
  const restMultiplier = insufficientRest ? 1.5 : 1.0;
  const regularCost = regularHours > 0 ? dayRate * restMultiplier : 0;
  const ot1Cost = ot1Hours * hourlyEquivalent * ot1Multiplier;
  const ot2Cost = ot2Hours * hourlyEquivalent * ot2Multiplier;

  return {
    hoursWorked,
    regularHours,
    ot1Hours,
    ot2Hours,
    regularCost,
    ot1Cost,
    ot2Cost,
    totalCost: regularCost + ot1Cost + ot2Cost,
    isHoliday: false,
    insufficientRest,
  };
}
